/**
 * InlineSkeleton - Ultra-lightweight skeleton for public menu
 * 
 * This component uses inline styles to avoid waiting for CSS bundles
 * to load. It renders immediately with the HTML, providing instant
 * visual feedback to users on slow connections.
 */

import React from 'react';

const pulseKeyframes = `
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const skeletonStyle: React.CSSProperties = {
  backgroundColor: '#e5e7eb',
  borderRadius: '0.375rem',
  animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
};

const Skeleton: React.FC<{ style?: React.CSSProperties; className?: string }> = ({ style, className }) => (
  <div style={{ ...skeletonStyle, ...style }} className={className} />
);

export function InlineSkeleton() {
  return (
    <>
      <style>{pulseKeyframes}</style>
      <div style={{ 
        minHeight: '100vh', 
        backgroundColor: '#f9fafb', 
        paddingBottom: '5rem',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
        {/* Header Skeleton */}
        <div style={{ backgroundColor: 'white', paddingBottom: '1rem', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
          {/* Banner/Cover */}
          <div style={{ height: '8rem', width: '100%', backgroundColor: '#e5e7eb' }} />
          
          {/* Restaurant Info */}
          <div style={{ 
            padding: '0 1rem', 
            marginTop: '-2rem', 
            position: 'relative', 
            zIndex: 10, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center' 
          }}>
            <div style={{ 
              width: '5rem', 
              height: '5rem', 
              borderRadius: '50%', 
              border: '4px solid white', 
              backgroundColor: 'white', 
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              overflow: 'hidden'
            }}>
              <Skeleton style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
            </div>
            <div style={{ marginTop: '0.75rem', textAlign: 'center', width: '100%', maxWidth: '20rem' }}>
              <Skeleton style={{ height: '1.5rem', width: '75%', margin: '0 auto 0.5rem' }} />
              <Skeleton style={{ height: '1rem', width: '50%', margin: '0 auto' }} />
            </div>
          </div>

          {/* Search Bar */}
          <div style={{ padding: '0 1rem', marginTop: '1.5rem' }}>
            <Skeleton style={{ height: '2.5rem', width: '100%', borderRadius: '0.5rem' }} />
          </div>

          {/* Categories */}
          <div style={{ 
            marginTop: '1.5rem', 
            padding: '0 1rem', 
            display: 'flex', 
            gap: '0.75rem', 
            overflow: 'hidden' 
          }}>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton 
                key={i} 
                style={{ 
                  height: '2rem', 
                  width: '6rem', 
                  borderRadius: '9999px', 
                  flexShrink: 0 
                }} 
              />
            ))}
          </div>
        </div>

        {/* Menu Items Grid */}
        <div style={{ padding: '1rem' }}>
          {[1, 2].map((category) => (
            <div key={category} style={{ marginBottom: '1.5rem' }}>
              <Skeleton style={{ height: '1.5rem', width: '8rem', marginBottom: '1rem' }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {[1, 2, 3].map((item) => (
                  <div 
                    key={item} 
                    style={{ 
                      backgroundColor: 'white', 
                      padding: '0.75rem', 
                      borderRadius: '0.75rem', 
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                      display: 'flex', 
                      gap: '0.75rem', 
                      height: '7rem' 
                    }}
                  >
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.25rem 0' }}>
                      <Skeleton style={{ height: '1rem', width: '75%' }} />
                      <Skeleton style={{ height: '0.75rem', width: '100%' }} />
                      <div style={{ paddingTop: '0.5rem' }}>
                        <Skeleton style={{ height: '1rem', width: '4rem' }} />
                      </div>
                    </div>
                    <Skeleton style={{ width: '6rem', height: '100%', borderRadius: '0.5rem', flexShrink: 0 }} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
